import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { DetailCards } from "./Sv4CardDetail";
import {
  apiMessage,
  approveRejectSv4Card,
  DEDUCTION_NOTE,
  findOneSv4Card,
  getLoggedInEmpId,
} from "./sv4Card.service";

interface RemarkModalProps {
  actionType: "A" | "R";
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (remark: string) => void;
}

const RemarkModal: React.FC<RemarkModalProps> = ({ actionType, isSubmitting, onClose, onConfirm }) => {
  const [remark, setRemark] = useState("");
  const isApprove = actionType === "A";
  const MAX = 500;

  const handleConfirm = () => {
    if (!isApprove && !remark.trim()) {
      toast.error("Please enter a remark");
      return;
    }
    onConfirm(remark);
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content border-0 shadow-lg rounded-3">
          <div className="modal-header border-bottom py-2 px-3">
            <h6 className="modal-title fw-bold text-dark mb-0">
              {isApprove ? "Approve Request" : "Reject Request"}
            </h6>
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: "0.8rem" }}
              onClick={() => !isSubmitting && onClose()}
            />
          </div>
          <div className="modal-body p-3">
            <p className="text-muted small mb-2" style={{ fontSize: "0.825rem" }}>
              {isApprove
                ? "You may add an approval remark (optional)."
                : "Please add your rejection reason before proceeding."}
            </p>

            <label className="form-label fw-semibold small text-dark mb-1" style={{ fontSize: "0.825rem" }}>
              Any Remarks{" "}
              {!isApprove && <span className="text-danger">*</span>}
              {isApprove && <span className="text-muted fw-normal">(Optional)</span>}
            </label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              maxLength={MAX}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={isApprove ? "Enter approval remark..." : "Enter rejection reason..."}
              style={{
                borderColor: isApprove ? "#198754" : "#dc3545",
                resize: "none",
                fontSize: "0.85rem",
              }}
              disabled={isSubmitting}
            />
            <div className="text-end text-muted small mt-1" style={{ fontSize: "0.75rem" }}>
              {remark.length}/{MAX}
            </div>
          </div>
          <div className="modal-footer border-top py-2 px-3 d-flex gap-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary px-4 py-2"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-sm px-4 py-2 text-white fw-semibold ${isApprove ? "btn-success" : "btn-danger"}`}
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  {isApprove ? "Approving..." : "Rejecting..."}
                </>
              ) : isApprove ? (
                "Approve"
              ) : (
                "Reject"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="py-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="card border-0 shadow-sm mb-4 placeholder-glow">
        <div className="card-header bg-white border-bottom">
          <span className="placeholder col-4 rounded" />
        </div>
        <div className="card-body">
          <div className="row">
            {[1, 2, 3, 4, 5, 6].map((j) => (
              <div key={j} className="col-md-4 mb-3">
                <span className="placeholder col-6 rounded d-block mb-1" style={{ height: 12 }} />
                <span className="placeholder col-10 rounded d-block" style={{ height: 16 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Sv4CardApproval: React.FC = () => {
  const routes = all_routes;
  const { requestID } = useParams<{ requestID: string }>();
  const navigate = useNavigate();
  const loggedInEmpId = getLoggedInEmpId();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [modalAction, setModalAction] = useState<"A" | "R" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!requestID) return;
    setLoading(true);
    setFetchError(null);
    try {
      const data = await findOneSv4Card(decodeURIComponent(requestID));
      if (data && typeof data === "object") {
        setDetail(data);
      } else {
        setFetchError("Request not found");
      }
    } catch (error: any) {
      const msg = error?.message || "Failed to load request details";
      setFetchError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [requestID]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleConfirm = async (remark: string) => {
    if (!detail || !modalAction) return;
    if (modalAction === "R" && !remark.trim()) {
      toast.error("Please enter a remark");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await approveRejectSv4Card({
        EmpID: String(detail?.EmpID ?? ""),
        ReferenceNumber: String(detail?.ReferenceNumber ?? ""),
        Action: modalAction,
        HRemarks: remark.trim(),
        ApproverUserID: String(loggedInEmpId || ""),
      });
      toast.success(
        apiMessage(
          res,
          modalAction === "A" ? "Approved successfully" : "Rejected successfully",
        ),
      );
      setModalAction(null);
      setTimeout(() => navigate(-1), 800);
    } catch (error: any) {
      toast.error(error?.message || (modalAction === "A" ? "Approval failed" : "Rejection failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = detail?.ReferenceNumber
    ? `Approve new SV4 card request - ${detail.ReferenceNumber}`
    : "SV4 Card Request Details";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{pageTitle}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">SV4 Approval</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            {!loading && (
              <button className="btn btn-outline-secondary btn-sm" onClick={fetchDetail} title="Refresh">
                <i className="ti ti-refresh me-1" />
                Refresh
              </button>
            )}
            <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
          </div>
        </div>

        {loading && <LoadingSkeleton />}

        {!loading && fetchError && (
          <div className="text-center py-5">
            <div
              className="rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center mx-auto mb-3"
              style={{ width: 72, height: 72 }}
            >
              <i className="ti ti-file-off text-danger" style={{ fontSize: 32 }} />
            </div>
            <p className="text-muted mb-3">{fetchError}</p>
            <button className="btn btn-primary" onClick={fetchDetail}>
              <i className="ti ti-refresh me-1" />
              Retry
            </button>
          </div>
        )}

        {!loading && !fetchError && detail && <DetailCards detail={detail} />}

        {!loading && !fetchError && detail && (
          <div className="border-start border-4 border-danger bg-danger-subtle px-3 py-2 rounded-end mb-4">
            <span className="text-danger fw-semibold small">* {DEDUCTION_NOTE}</span>
          </div>
        )}

        {!loading && !fetchError && detail && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body py-3 px-4 d-flex justify-content-end gap-3">
              <button
                className="btn btn-danger px-4 py-2 fw-semibold"
                onClick={() => setModalAction("R")}
                disabled={isSubmitting}
              >
                Reject
              </button>
              <button
                className="btn btn-success px-4 py-2 fw-semibold"
                onClick={() => setModalAction("A")}
                disabled={isSubmitting}
              >
                Approve
              </button>
            </div>
          </div>
        )}
      </div>

      {modalAction && (
        <RemarkModal
          actionType={modalAction}
          isSubmitting={isSubmitting}
          onClose={() => !isSubmitting && setModalAction(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export default Sv4CardApproval;
